import { defineSettingsScreen } from '@heidi/runtime/shell';

/**
 * The Notifications and Memory settings screen bodies.
 *
 * Each `id` is the settings route slug (`ROUTE_TO_SLUG['/settings/notifications']`
 * and `ROUTE_TO_SLUG['/settings/memory']`), which is what lets a package serve
 * existing settings screens with the sidebar and the IA untouched: the loader
 * resolves each route to its contribution and falls back to the app's own
 * importer for every route no package claims.
 *
 * `section: 'personal'` is measured, not chosen — both screens render
 * `group=` the "Personal" message in `SettingsPageHeader` and `useSettingsList`
 * lists them under that group.
 *
 * `order` is the position `useSettingsList` renders each item at inside the
 * Personal group today — memory second (1) and notifications last (5), behind
 * account, display-controls, data-management and defaults. It is inert: screens
 * are served by id and the sidebar is hand-written, so recording the real
 * positions claims nothing anyone else has to renumber around, and leaving a
 * gap where the four app-side screens sit is the honest record rather than
 * renumbering them to 0 and 1.
 *
 * `builds: 'all'`: measured. Neither screen is build-gated — `useSettingsList`
 * renders both under the same conditions on every profile, and the on-prem
 * appliance resolves both routes today — so a narrower value would withhold a
 * screen the appliance already serves.
 *
 * The thunks name the exports rather than relying on a default one: these are
 * named exports like every other component in this package, and
 * `ContributionLoadThunk` wants `{ default }`. Each thunk points at the SCREEN
 * MODULE, never at the `.` door — the door also carries the hydration-gate
 * panel and this subtree's host seam, and a contribution is the one consumer
 * that can name the module directly.
 */
export const notificationsSettingsScreen = defineSettingsScreen({
  id: 'notifications',
  section: 'personal',
  order: 5,
  load: () =>
    import('../ui/settings/notifications/NotificationsSettings').then((module) => ({
      default: module.NotificationsSettings,
    })),
  builds: 'all',
});

export const memorySettingsScreen = defineSettingsScreen({
  id: 'memory',
  section: 'personal',
  order: 1,
  load: () =>
    import('../ui/settings/memory/MemorySettings').then((module) => ({
      default: module.MemorySettings,
    })),
  builds: 'all',
});
